import { LegalLayout, LegalList, LegalSection } from "../../components/public/legal-layout";

const lastUpdated = "24 March 2026";

export default function LegalPrivacyPage({ auth }) {
  return (
    <LegalLayout
      auth={auth}
      eyebrow="Legal"
      title="Privacy Policy & Cookies"
      intro="This Privacy Policy & Cookies notice explains how MDG handles personal data when users access UGC Persona. It is written for an international customer base and follows a general EU-oriented privacy structure, including transparency around account data, billing, AI provider processing, and technical cookies."
    >
      <LegalSection title="1. Data Controller">
        <p>
          The controller for the UGC Persona service is <span className="font-semibold text-white">MDG</span>.
        </p>
        <p>
          Contact:{" "}
          <a href="mailto:hello@ugcpersona.com" className="text-cyan-200 underline underline-offset-4">
            hello@ugcpersona.com
          </a>
        </p>
      </LegalSection>

      <LegalSection title="2. Scope of this Policy">
        <p>
          This policy applies to personal data processed when you visit the public site, create an account, use the generation workflows,
          contact support, manage billing, or interact with integrated providers that are necessary to deliver the service.
        </p>
      </LegalSection>

      <LegalSection title="3. Categories of Data We Process">
        <LegalList
          items={[
            "Account data, such as name, email address, password hash, locale, and timezone.",
            "Subscription and billing data, such as plan, credit balance, Stripe customer identifiers, and invoice-related records.",
            "Usage data, including generation logs, selected models, provider metadata, request timing, and consumption events.",
            "User-submitted content, including prompts, uploaded assets, script text, generated copy, and generation settings.",
            "Support data, including messages sent to MDG by email or through support channels.",
            "Technical data, such as device, browser, IP-derived diagnostics, session information, and security logs."
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Why We Process Your Data">
        <LegalList
          items={[
            "To create and manage user accounts.",
            "To operate subscriptions, billing, credits, renewals, and payment flows.",
            "To process generation requests and return outputs.",
            "To maintain security, detect abuse, prevent fraud, and troubleshoot issues.",
            "To provide customer support and service communications.",
            "To comply with legal obligations, accounting obligations, and provider audit requirements."
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Legal Bases">
        <p>Where EU or similar privacy rules apply, MDG generally relies on the following legal bases:</p>
        <LegalList
          items={[
            "Performance of a contract, where data is needed to provide the service you requested.",
            "Legitimate interests, such as service security, abuse prevention, product reliability, and operational analytics necessary to run the platform.",
            "Legal obligations, where retention or disclosure is required by law, tax, accounting, or regulatory duties.",
            "Consent, where this becomes relevant for optional features or non-essential technologies in the future."
          ]}
        />
      </LegalSection>

      <LegalSection title="6. AI Providers and Service Providers">
        <p>
          UGC Persona relies on third-party infrastructure and AI providers to deliver the service. Depending on your workflow and configuration,
          relevant request data may be sent to providers such as fal.ai, Wavespeed, OpenRouter, ElevenLabs, and Stripe.
        </p>
        <p>
          These providers may process prompts, uploaded assets, script text, usage metadata, and billing references as needed to perform the
          requested operation. MDG only intends to share the data reasonably necessary to provide the requested workflow.
        </p>
      </LegalSection>

      <LegalSection title="7. International Transfers">
        <p>
          UGC Persona serves worldwide customers and uses infrastructure and providers that may operate outside your country. Your data may
          therefore be processed in multiple jurisdictions. Where required, MDG intends to rely on appropriate transfer mechanisms and
          contractual protections that are commercially reasonable for the services used.
        </p>
      </LegalSection>

      <LegalSection title="8. Data Retention">
        <LegalList
          items={[
            "Account and subscription records are retained for as long as reasonably necessary to provide the service and maintain billing history.",
            "Provider usage and credit logs may be retained for operational, fraud-prevention, dispute, and accounting purposes.",
            "Support communications may be retained for continuity and quality control.",
            "Certain records may be retained longer where required by law, tax, accounting, security, or dispute-resolution needs."
          ]}
        />
      </LegalSection>

      <LegalSection title="9. Your Rights">
        <p>Depending on your location and applicable law, you may have rights to access, correct, delete, restrict, or object to certain processing.</p>
        <p>
          You may also have the right to data portability and to lodge a complaint with a competent supervisory authority. To exercise privacy
          requests, contact{" "}
          <a href="mailto:hello@ugcpersona.com" className="text-cyan-200 underline underline-offset-4">
            hello@ugcpersona.com
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="10. Security">
        <p>
          MDG applies commercially reasonable technical and organizational measures intended to protect account and service data. However, no
          method of storage or transmission is completely secure, and absolute security cannot be guaranteed.
        </p>
      </LegalSection>

      <LegalSection title="11. Cookies and Similar Technologies">
        <p>UGC Persona currently uses technical and essential cookies only.</p>
        <LegalList
          items={[
            "Authentication cookies, needed to keep users signed in and protect account sessions.",
            "Security-related cookies or session tokens, needed to prevent misuse, cross-site request forgery, and unauthorized access.",
            "Preference or strictly functional storage, where necessary for core product behavior."
          ]}
        />
        <p>
          At this time, this policy is written on the basis that non-essential advertising or tracking cookies are not intentionally deployed as
          part of the core product experience. If that changes, this page should be updated before or at the time such technologies are
          introduced.
        </p>
      </LegalSection>

      <LegalSection title="12. Children">
        <p>
          The service is not intended for children under the age required by applicable law to enter into online services contracts. If MDG
          becomes aware that personal data has been collected in breach of applicable age restrictions, it may take reasonable steps to delete
          that information.
        </p>
      </LegalSection>

      <LegalSection title="13. Policy Changes">
        <p>
          MDG may update this Privacy Policy & Cookies notice from time to time to reflect operational, legal, or product changes. The most
          recent version will be made available on this page.
        </p>
        <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Last updated: {lastUpdated}</p>
      </LegalSection>
    </LegalLayout>
  );
}
